import { useMutation } from "@tanstack/react-query";
import type {
  GenerateManagementReportNewRequest,
  GenerateManagementReportRequest,
  GenerateManagementYearSummaryReportRequest,
  GenerateConsolidatedReportRequest,
  GenerateNewsReportRequest,
  GenerateSwimmersReportRequest,
} from "../../../api/client/types/report.types";
import type { ApiError } from "../../../api/client/types/common.types";
import { reportEndpoints } from "../../../api/endpoints/report";

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
const reportQueryKeys = {
  root: ["reports"] as const,
  generateSwimmers: () =>
    [...reportQueryKeys.root, "generateSwimmers"] as const,
  generateNews: () => [...reportQueryKeys.root, "generateNews"] as const,
  generateConsolidated: () =>
    [...reportQueryKeys.root, "generateConsolidated"] as const,
  generateManagement: () =>
    [...reportQueryKeys.root, "generateManagement"] as const,
  /** TODO: merge into generateManagement once the legacy endpoint is removed. */
  generateManagementNew: () =>
    [...reportQueryKeys.root, "generateManagementNew"] as const,
  generateManagementYearSummary: () =>
    [...reportQueryKeys.root, "generateManagementYearSummary"] as const,
};

/**
 * Hook: useGenerateSwimmersReport
 * Generates and downloads an Excel report for swimmers registered in a specific date range.
 * 
 * Accepts start_date and end_date in DD/MM/YYYY format (both inclusive) and an optional building_id.
 * The date range cannot exceed 32 days.
 * 
 * This mutation triggers a file download, so it doesn't return data to cache.
 * The actual file download is handled in the component using the mutation.
 */
export const useGenerateSwimmersReport = () => {
  return useMutation<Blob, ApiError, GenerateSwimmersReportRequest>({
    mutationKey: reportQueryKeys.generateSwimmers(),
    mutationFn: (params) =>
      reportEndpoints.generateSwimmersReport(params),
  });
};

/**
 * Hook: useGenerateNewsReport
 * Generates and downloads an Excel report for news registered in a specific month and year.
 * 
 * This mutation triggers a file download, so it doesn't return data to cache.
 * The actual file download is handled in the component using the mutation.
 */
export const useGenerateNewsReport = () => {
  return useMutation<Blob, ApiError, GenerateNewsReportRequest>({
    mutationKey: reportQueryKeys.generateNews(),
    mutationFn: (params) =>
      reportEndpoints.generateNewsReport(params),
  });
};

/** Generates the all-buildings consolidated Excel workbook. */
export const useGenerateConsolidatedReport = () => {
  return useMutation<Blob, ApiError, GenerateConsolidatedReportRequest>({
    mutationKey: reportQueryKeys.generateConsolidated(),
    mutationFn: (params) =>
      reportEndpoints.generateConsolidatedReport(params),
  });
};

/**
 * Hook: useGenerateManagementReport
 * Generates and downloads the management report PDF (reporte gerencial) for a building and date range.
 *
 * Requires start_date, end_date (DD/MM/YYYY) and building_id. Pass year_summary: 1 to include the annual summary page.
 * This mutation triggers a file download (PDF), so it doesn't return data to cache.
 */
export const useGenerateManagementReport = () => {
  return useMutation<Blob, ApiError, GenerateManagementReportRequest>({
    mutationKey: reportQueryKeys.generateManagement(),
    mutationFn: (params) =>
      reportEndpoints.generateManagementReport(params),
  });
};

/**
 * Hook: useGenerateManagementReportNew
 * Generates and downloads the new management report PDF (refactored template).
 *
 * Uses GET /reports/management-new — parametrized interpretation pages and
 * dynamic risk-event tables. Same parameters as useGenerateManagementReport.
 *
 * TODO: once the frontend fully migrates to this hook, rename it to
 * useGenerateManagementReport and remove the legacy hook and endpoint.
 */
export const useGenerateManagementReportNew = () => {
  return useMutation<Blob, ApiError, GenerateManagementReportNewRequest>({
    mutationKey: reportQueryKeys.generateManagementNew(),
    mutationFn: (params) =>
      reportEndpoints.generateManagementReportNew(params),
  });
};

/**
 * Hook: useGenerateManagementYearSummaryReport
 * Generates and downloads the 3-page yearly management report PDF
 * (cover, yearly summary, closing) for a building and calendar year.
 *
 * Requires year (YYYY) and building_id. This mutation triggers a file download (PDF),
 * so it doesn't return data to cache.
 */
export const useGenerateManagementYearSummaryReport = () => {
  return useMutation<
    Blob,
    ApiError,
    GenerateManagementYearSummaryReportRequest
  >({
    mutationKey: reportQueryKeys.generateManagementYearSummary(),
    mutationFn: (params) =>
      reportEndpoints.generateManagementYearSummaryReport(params),
  });
};

